import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { downloadCSV } from "utils/helperFunctions";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import UploadCsvButton from "app/storybookComponents/Button/UploadCsvButton";
import { CSVUploadTableStructureError } from "app/storybookComponents/Button/types";
import { PEOPLE_COLUMNS } from "app/containers/AdvancedCsvUpload/constants";
import { RawRowData } from "app/containers/AdvancedCsvUpload/types";
import ExportCSVButton from "../../storybookComponents/Button/ExportCSVButton";
import { useNavigate } from "react-router-dom";
import {
  getCSVFieldMappings,
  getErrorsByType,
  previewInviteUsersViaCSV,
  selectCSVFieldMappings,
  selectCsvUploadOptions,
  selectGetCSVFieldMappingsStatus,
  selectManagerAndDirectReportsByUserAccountId,
  setCsvUploadOptions,
  setUploadedData,
} from "app/containers/AdvancedCsvUpload/slice";
import { Collapse } from "react-bootstrap";
import Toggle from "app/components/Toggle";
import {
  selectDepartments,
  selectIsDepartmentsHidden,
} from "app/containers/AdminConsole/slice";
import {
  selectAllCompanyUsersById,
  selectIsCurrentUserAdmin,
} from "app/containers/Global/slice";
import Loading from "app/storybookComponents/Loading";
import { normalizePeopleRows, getUploadCSVPeopleColumns } from "./helpers";

const TEMPLATE_COLUMNS_MAP: Record<
  string,
  { label: string; isRequired?: boolean }
> = {
  firstName: { label: "First Name", isRequired: true },
  lastName: { label: "Last Name", isRequired: true },
  emailAddress: { label: "Email", isRequired: true },
  jobTitle: { label: "Job Title" },
  gender: { label: "Gender" },
  managerEmailAddress: { label: "Manager Email" },
};

export default function UploadUserCSVForm() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const areDepartmentsHidden = useAppSelector(selectIsDepartmentsHidden);
  const usersInfoById = useAppSelector(selectAllCompanyUsersById);
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const managersAndDirectReportsByUserAccountId = useAppSelector(
    selectManagerAndDirectReportsByUserAccountId
  );
  const csvFieldMappings = useAppSelector(selectCSVFieldMappings);
  const csvFieldMappingsStatus = useAppSelector(selectGetCSVFieldMappingsStatus);
  const departments = useAppSelector(selectDepartments);

  useEffect(() => {
    if (csvFieldMappingsStatus === "idle") {
      dispatch(getCSVFieldMappings());
    }
  }, [dispatch, csvFieldMappingsStatus]);

  const [structureError, setStructureError] =
    useState<CSVUploadTableStructureError | null>(null);
  const [error, setError] = useState<"file-size-error" | "empty-file" | null>(
    null
  );
  const [isUploadingLoading, setIsUploadingLoading] = useState(false);
  const [checkingTableStructure, setCheckingTableStructure] = useState(false);
  const [isViewCSVRequirementsOpened, setIsViewCSVRequirementsOpened] =
    useState(false);
  const csvUploadOptions = useAppSelector(selectCsvUploadOptions);
  const addNewEmployees = csvUploadOptions.addNewEmployees === 1;
  const updateExistingEmployees =
    csvUploadOptions.updateExistingEmployees === 1;
  const deactivateEmployeesNotInCSV =
    csvUploadOptions.deactivateEmployeesNotInCSV === 1;

  const downloadTemplateCSV = () => {
    const data: string[][] = [
      getUploadCSVPeopleColumns(csvFieldMappings).map((col) => col.displayName),
    ];
    data.push(Object.values(PEOPLE_COLUMNS).map(() => "")); // Add an empty row
    downloadCSV("template", data);
  };

  const getExistingUsersCSVRows = () => {
    const csvRows: {
      [headerKey: string]: string | number;
    }[] = [];

    Object.values(usersInfoById).forEach((user) => {
      const managerUserAccountId =
        managersAndDirectReportsByUserAccountId[user.userAccountId]
          ?.managerRelationship?.managerUserAccountId;
      const managerEmailAddress = managerUserAccountId
        ? usersInfoById[managerUserAccountId]?.emailAddress
        : "";
      const department =
        (user?.departmentId && departments[user.departmentId]?.name) ?? "";
      const additionalUserInfo = user?.additionalUserInfo ?? {};

      csvRows.push({
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        emailAddress: user.emailAddress ?? "",
        jobTitle: user.jobTitle ?? "",
        gender: user.gender ?? "",
        managerEmailAddress: managerEmailAddress ?? "",
        ...(areDepartmentsHidden ? {} : { department }),
        ...additionalUserInfo,
      });
    });

    return csvRows;
  };

  const getExistingUsersCSVHeaders = () => {
    const headers: {
      key?: string;
      label: string;
    }[] = [
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "emailAddress", label: "Email" },
      { key: "jobTitle", label: "Job Title" },
      { key: "gender", label: "Gender" },
      { key: "managerEmailAddress", label: "Manager Email" },
    ];

    if (!areDepartmentsHidden) {
      headers.push({
        key: "department",
        label: "Department",
      });
    }

    const additionalUserFields = csvFieldMappings?.additionalUserFields ?? [];
    for (const field of additionalUserFields) {
      const key = field.displayName;
      headers.push({
        key,
        label: field.displayName,
      });
    }

    return headers;
  };

  const handleCSVUploadSuccess = async (payload: RawRowData[]) => {
    setIsUploadingLoading(true);
    const newUpdatedData = normalizePeopleRows(payload, csvFieldMappings);
    // We need to make the request to the backend so we can validate the emails
    dispatch(setUploadedData(newUpdatedData));
    await dispatch(
      previewInviteUsersViaCSV({
        rows: newUpdatedData,
      })
    );
    dispatch(getErrorsByType());
    setIsUploadingLoading(false);
    navigate(`/AdvancedCsvUpload`);
  };

  const getCSVUploadButton = (buttonText: string = "Upload .csv") => (
    <UploadCsvButton
      resetUpload={() => {
        setStructureError(null);
        setError(null);
      }}
      onCSVUploadSuccess={handleCSVUploadSuccess}
      templateColumns={getUploadCSVPeopleColumns(csvFieldMappings)}
      setStructureError={setStructureError}
      setError={setError}
      setLoading={setCheckingTableStructure}
      buttonText={
        <>
          <FontAwesomeIcon icon="upload" className="me-2" />
          {buttonText}
        </>
      }
      maxRows={5000}
      disabled={
        !(
          addNewEmployees ||
          updateExistingEmployees ||
          deactivateEmployeesNotInCSV
        )
      }
    />
  );

  const getCheckingComponent = () => {
    if (isUploadingLoading || checkingTableStructure) {
      return (
        <div className="column-gap-12px">
          <h3>Checking File Structure...</h3>
          <Loading color="gray" />
        </div>
      );
    }

    if (structureError) {
      return (
        <div className="warning-banner red">
          <p>
            Please ensure your file has the following required columns: "First
            Name", "Last Name", "Email"
          </p>
        </div>
      );
    }

    if (error === "empty-file") {
      return (
        <div className="warning-banner red">
          <p>
            Sorry, the file you uploaded is empty. Please double check your file
            and try again.
          </p>
        </div>
      );
    }

    if (error === "file-size-error") {
      return (
        <div className="warning-banner red">
          <p>
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
            You can not process more than 5,000 users at one time. Please
            re-upload your file and try again.
          </p>
        </div>
      );
    }

    return null;
  };

  const handleToggleChange = (
    key: keyof typeof csvUploadOptions,
    currentValue: boolean
  ) => {
    dispatch(
      setCsvUploadOptions({
        [key]: currentValue ? 0 : 1,
      })
    );
  };

  const getToggleOption = (
    label: string,
    state: boolean,
    optionKey: keyof typeof csvUploadOptions
  ) => {
    return (
      <div className="d-flex">
        <div>
          <Toggle
            isOn={state}
            handleToggle={() => handleToggleChange(optionKey, state)}
            size="sm"
          />
        </div>
        <p className="ms-2">{label}</p>
      </div>
    );
  };

  const getImportOptions = () => {
    return (
      <div className="column-gap-16px">
        <p style={{ marginTop: "-4px", fontWeight: "bold" }}>Import Options</p>
        {getToggleOption(
          "Add new employees",
          addNewEmployees,
          "addNewEmployees"
        )}
        {getToggleOption(
          "Update existing employees",
          updateExistingEmployees,
          "updateExistingEmployees"
        )}
        {getToggleOption(
          "Deactivate employees who are not found in import",
          deactivateEmployeesNotInCSV,
          "deactivateEmployeesNotInCSV"
        )}
      </div>
    );
  };

  const getAcceptableColumnHeaders = () => {
    const headers: {
      label: string;
      isRequired: boolean;
    }[] = [];
    const templateMapEntries = Object.entries(TEMPLATE_COLUMNS_MAP);

    for (const [key, value] of templateMapEntries) {
      let header: string = value.label;
      if (csvFieldMappings?.activeSystemFields?.[key]) {
        header = csvFieldMappings?.activeSystemFields?.[key];
      }
      headers.push({ label: header, isRequired: !!value.isRequired });
    }

    if (!areDepartmentsHidden) {
      headers.push({
        label:
          csvFieldMappings?.activeSystemFields?.["department"] ?? "Department",
        isRequired: false,
      });
    }

    for (const field of csvFieldMappings?.additionalUserFields ?? []) {
      headers.push({
        label: field.displayName,
        isRequired: false,
      });
    }

    return headers;
  };

  const getViewCsvRequirements = () => {
    const acceptableColumnHeaders = getAcceptableColumnHeaders();
    return (
      <div className="column-gap-20px">
        <button
          className="button-link"
          onClick={() =>
            setIsViewCSVRequirementsOpened(!isViewCSVRequirementsOpened)
          }
        >
          View CSV Format Requirements
        </button>
        <Collapse
          in={isViewCSVRequirementsOpened}
          onExited={() => setIsViewCSVRequirementsOpened(false)}
        >
          <div>
            <p>
              CSV files should be formatted with{" "}
              {acceptableColumnHeaders.length} columns:
            </p>
            <ul style={{ fontSize: "14px" }}>
              {acceptableColumnHeaders.map((header) => (
                <li key={header.label}>
                  {header.label}
                  {header.isRequired ? "" : " (Optional)"}
                </li>
              ))}
            </ul>
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <div>
      <div className="column-gap-20px">
        <div>
          <p className="mb-0">
            To invite users via .csv file, please download this{" "}
            <span
              onClick={() => downloadTemplateCSV()}
              role="button"
              className="link-primary"
            >
              template (.csv)
            </span>
          </p>
          {isAdmin ? (
            <div className="mb-0 row-gap-4px">
              <p>Or use existing users as base for import </p>
              <ExportCSVButton
                headers={getExistingUsersCSVHeaders()}
                getRows={getExistingUsersCSVRows}
                fileName="existing-users"
                buttonText="existing users (.csv)"
                variant="link"
                className="link-primary"
              />
            </div>
          ) : null}
        </div>
        <p>Then, upload the completed file below:</p>
        <div>
          {getCSVUploadButton()}
          <p style={{ color: "#86888b", marginTop: "10px" }}>
            File size limit: 5 MB
          </p>
        </div>
        {getViewCsvRequirements()}
        {getImportOptions()}
        {getCheckingComponent()}
      </div>
    </div>
  );
}
